import { createContext } from 'react'
import type { ChecklistBreadcrumbSegment } from '../utils/checklistBreadcrumb'
import type { ChecklistWorkflowStatus } from '../types/schema'

export type ChecklistDetailPopupMode = 'view' | 'edit'

export type OpenChecklistDetailOptions = {
  mode?: ChecklistDetailPopupMode
  /** Location shown in the popup header. Callers supply their current-view
   *  breadcrumb (Board / Manage / Canvas etc.) so the popup reflects where
   *  the user opened it from. If omitted, the host derives a default from
   *  the item's parent chain. */
  breadcrumb?: ChecklistBreadcrumbSegment[]
  /** For DAILY / WEEKLY items: which occurrence to focus when the wizard
   *  opens. Callers that already know the instance (Recurring Canvas day/week
   *  views, notifications targeting a specific date, calendar surfaces) pass
   *  their page key here. When omitted the host focuses today's occurrence. */
  initialOccurrenceKey?: string | null
  /** Target workflow status to pre-select, evaluating blockers on open */
  pendingStatus?: ChecklistWorkflowStatus
}

export type OpenChecklistDraftOptions = {
  workspaceId: string
  /** Board the new checklist should belong to (usually 'DASHBOARD' from the calendar). */
  board: 'DASHBOARD' | 'VISIONBOARD' | 'ARCHIVE' | 'BIN'
  /** Pre-fill scheduleStartDate (ISO YYYY-MM-DD). */
  scheduleStart: string
  /** Pre-fill scheduleEndDate (ISO YYYY-MM-DD). Same as scheduleStart for single-day. */
  scheduleEnd: string
  /** Optional pre-selected parent. Defaults to the workspace-default container. */
  initialParentId?: string | null
  /** Breadcrumb the draft should display (typically 'Calendar' or the caller's location). */
  breadcrumb?: ChecklistBreadcrumbSegment[]
}

export type ChecklistDetailPopupContextValue = {
  activeChecklistItemId: string | null
  mode: ChecklistDetailPopupMode
  breadcrumb: ChecklistBreadcrumbSegment[] | null
  initialOccurrenceKey: string | null
  activeDraft: OpenChecklistDraftOptions | null
  openChecklistDetail: (itemId: string, options?: OpenChecklistDetailOptions) => void
  openChecklistDraft: (input: OpenChecklistDraftOptions) => void
  closeChecklistDetail: () => void
}

export const ChecklistDetailPopupContext = createContext<ChecklistDetailPopupContextValue | undefined>(undefined)
